import { MutationApi, PatchApi } from "./types.mjs";
import { Database } from "./Database.mjs";
import { ConsoleLogger } from "./Logger.mjs";
import { q } from "./query/Query.mjs";
import { SyncEngine } from "./sync/SyncEngine.mjs";
import { tag } from "@repo/utils/typeUtils";
import { randomId } from "@repo/utils/randomId";

//#region src/TandemClient.ts
var TandemClient = class {
	db;
	/**
	* Resolves when initial load from storage completes
	*/
	ready;
	clientId;
	syncEngine;
	logger;
	rng;
	speculativeMutations = [];
	constructor({ storage: storageAdapter, remote, logger, autoConnect = true, syncInterval = 150, rng, minLogLevel = "log", onTransientError, onTransientErrorRecovered, onMaxRetriesExceeded, clientId }) {
		this.rng = rng ?? { randomId };
		this.clientId = clientId ?? tag(this.rng.randomId());
		this.logger = logger ?? new ConsoleLogger(["tandem-client"], minLogLevel);
		this.syncEngine = remote ? new SyncEngine({
			remote,
			clientId: this.clientId,
			handleRollback: (mutationsToRollback) => {
				this.rollback(mutationsToRollback);
			},
			applyPatchAt: (args) => this.applyPatchAt(args),
			autoConnect,
			logger: this.logger.scope("sync-engine"),
			syncInterval,
			onTransientError,
			onTransientErrorRecovered,
			onMaxRetriesExceeded
		}) : void 0;
		this.db = new Database({
			logger: this.logger.scope("db"),
			storage: storageAdapter,
			rng: this.rng
		});
		this.ready = this.db.ready;
	}
	pullFromRemote() {
		this.logger.info("pullFromRemote called");
		return this.syncEngine?.queuePull();
	}
	applyPatchAt({ patch, lastMutationId }) {
		this.logger.info("applyPatchAt called", {
			lastMutationId,
			patchSetLength: patch.set?.length,
			patchRemoveLength: patch.remove?.length
		});
		if (patch.set?.length === 0 && patch.remove?.length === 0) return;
		const commitedMutationIndex = this.speculativeMutations.findIndex((m) => m.id === lastMutationId);
		const tx = this.db.makeTupleDbTransaction();
		const inverted = MutationApi.getRollbackWrites(this.speculativeMutations);
		tx.write(inverted);
		const writeOps = PatchApi.toWriteOps(patch);
		tx.write(writeOps);
		const stillSpeculative = this.speculativeMutations.slice(commitedMutationIndex + 1);
		for (const mutation of stillSpeculative) tx.write(MutationApi.toWriteOps(mutation.ops));
		tx.commit();
		this.speculativeMutations = stillSpeculative;
	}
	rollback(mutationsToRollback) {
		this.logger.info("rollback called", { mutationsToRollbackCount: mutationsToRollback.length });
		const failedMutationIds = new Set(mutationsToRollback.map((m) => m.id));
		this.speculativeMutations = this.speculativeMutations.filter((m) => !failedMutationIds.has(m.id));
		const inverted = MutationApi.getRollbackWrites(mutationsToRollback);
		const tx = this.db.makeTupleDbTransaction();
		tx.write(inverted);
		tx.commit();
	}
	run(collection, queryFn) {
		this.logger.info("run called", { collection });
		const query = queryFn(q(collection));
		return this.db.run(query);
	}
	subscribe(collection, queryFn, callback) {
		this.logger.info("subscribe called", { collection });
		const query = queryFn(q(collection));
		const { result, destroy } = this.db.subscribe(query, callback);
		const unsubscribe = this.syncEngine?.subscribe(query.build());
		return {
			result,
			destroy: () => {
				unsubscribe?.();
				destroy();
			}
		};
	}
	transact() {
		this.logger.info("transact called");
		return this.db.transact();
	}
	commit(transaction) {
		this.logger.info("commit called", { opsCount: transaction.ops.length });
		if (transaction.ops.length === 0) return;
		const mutation = {
			ops: transaction.ops,
			id: tag(transaction.tupleDbTx.id)
		};
		this.db.commit(transaction);
		this.speculativeMutations.push(mutation);
		return this.syncEngine?.queuePush(mutation);
	}
	async connect() {
		this.logger.info("connect called");
		if (!this.syncEngine) throw new Error("Attempted to connect without a remote server configured");
		return await this.syncEngine.connect();
	}
	async disconnect() {
		this.logger.info("disconnect called");
		if (!this.syncEngine) {
			console.warn("Attempted to disconnect without a remote server configured");
			return;
		}
		return await this.syncEngine.disconnect();
	}
	async clear() {
		this.logger.info("clear called");
		this.speculativeMutations = [];
		await this.db.clear();
	}
};

//#endregion
export { TandemClient };